import { useRef } from 'react';
import { Link, useLocation } from '@tanstack/react-router';

import { useActiveIndicator } from '../../lib/motion/hooks';

import { NavLabel } from './NavLabel';
import { folderForPath, isSettingsPath, type NavLinkItem, type NavModel } from './navModel';

/**
 * The pages of the open folder. The ink pill slides to the page you open, and
 * a light copy of the labels, cut to the pill, keeps every label readable
 * while it travels.
 */
function PillTabs({ links, label }: { links: NavLinkItem[]; label: string }) {
  const { pathname } = useLocation();
  const pills = useRef<HTMLElement>(null);
  useActiveIndicator(pills, ".hs-pill[data-status='active']", pathname);
  return (
    <nav className="hs-pills" aria-label={label} ref={pills}>
      {links.map((link) => (
        <Link
          key={link.to}
          to={link.to}
          className="hs-pill"
          activeOptions={{ exact: link.exact ?? false }}
          activeProps={{ 'data-status': 'active', 'aria-current': 'page' }}
        >
          <NavLabel label={link.label} lock={link.lock} />
        </Link>
      ))}
      <span className="ind hs-pill-thumb" aria-hidden="true" />
      <span className="hs-pills-lit" aria-hidden="true">
        {links.map((link) => (
          <span key={link.to} className="hs-pill">
            <NavLabel label={link.label} lock={link.lock} decorative />
          </span>
        ))}
      </span>
    </nav>
  );
}

/** The Ajustes index, left of every settings page, like the Helios CRM's. */
export function SettingsIndex({ links }: { links: NavLinkItem[] }) {
  return (
    <nav className="hs-settings-index" aria-label="Ajustes">
      <p className="hs-settings-title">Ajustes</p>
      {links.map((link) => (
        <Link key={link.to} to={link.to} className="hs-settings-link" activeProps={{ 'data-status': 'active', 'aria-current': 'page' }}>
          <NavLabel label={link.label} lock={link.lock} />
        </Link>
      ))}
      <Link to="/configuracion" className="hs-settings-link" activeProps={{ 'data-status': 'active', 'aria-current': 'page' }}>
        Apariencia
      </Link>
    </nav>
  );
}

/**
 * What sits at the top of the sheet: the pages of the open folder as pills
 * when it holds more than one, or the Ajustes index beside a settings page.
 */
export function useSheetNav(model: NavModel) {
  const { pathname } = useLocation();
  const settings = isSettingsPath(model, pathname) || pathname === '/configuracion';
  const folder = settings ? undefined : folderForPath(model, pathname);
  const pills = folder && folder.links.length > 1 ? <PillTabs links={folder.links} label={folder.label} /> : null;
  return { settings, pills };
}
